import { Component, signal, inject, input, output, computed, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { ContextService, FeatureFlagService } from '@lr/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { ClientService } from '../../services/client.service';

interface NavSubItem {
  label: string;
  route: string;
  icon: string;
  description?: string;
  requiredPermission?: string;
  /** When true, only exact URL matches activate this item (avoids /members matching /members/add-member). */
  routerLinkExact?: boolean;
  /**
   * Extra path prefixes this item owns (detail/edit pages whose URLs live outside `route`).
   * Extend here when a page highlights nothing or wrongly highlights Dashboard.
   */
  activeRoutes?: string[];
  /** When set, this item is hidden if the named feature flag is disabled. */
  featureFlag?: string;
}

interface NavItem {
  label: string;
  route?: string;
  icon: string;
  items?: NavSubItem[];
  requiredPermission?: string;
}

const COMPANIES_NAV_ROUTE = '/account/subgroups';

/**
 * Client Portal specific navigation
 */
@Component({
  selector: 'app-navigation',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatListModule,
    MatIconModule,
    MatExpansionModule,
    MatButtonModule
  ],
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss']
})
export class NavigationComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private contextService = inject(ContextService);
  private clientService = inject(ClientService);
  private featureFlagService = inject(FeatureFlagService);
  private destroy$ = new Subject<void>();
  expandedGroups = signal<Set<string>>(new Set());
  userProfileExpanded = signal<boolean>(false);
  companyNavLabel = signal<'Company' | 'Companies'>('Companies');

  userName = input<string | null>(null);
  userEmail = input<string | null>(null);
  userRoles = input<string[]>([]);
  logoutClick = output<void>();
  closeMenu = output<void>();

  // Get user permissions as a signal from ContextService
  private userPermissions = toSignal(
    this.contextService.permissions$.pipe(
      map(permissions => permissions?.permissions || [])
    ),
    { initialValue: [] }
  );

  ngOnInit(): void {
    this.contextService.selectedClient$
      .pipe(
        map(client => client?.mCode ?? null),
        distinctUntilChanged(),
        switchMap(mCode => {
          if (!mCode) {
            return of(null);
          }
          return this.clientService.getSubclients(mCode).pipe(
            catchError(() => of([]))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(subclients => {
        if (!subclients) {
          this.companyNavLabel.set('Companies');
          return;
        }
        this.companyNavLabel.set(subclients.length === 1 ? 'Company' : 'Companies');
      });

    // Seed the active group as expanded on load and on each navigation.
    // This only ever *adds* to the set, so a group the user manually
    // collapsed (which does not trigger navigation) stays collapsed.
    this.expandActiveGroup();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.expandActiveGroup());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleUserProfile(): void {
    this.userProfileExpanded.update(expanded => !expanded);
  }

  getInitials(name: string | null): string {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  getRoleDisplayName(role: string | null): string {
    if (!role) return 'User';
    const roleMap: Record<string, string> = {
      PortalAdmin: 'Portal Admin',
      'portal-admin': 'Portal Admin',
      'billing-admin': 'Billing Admin',
      'billing-view-only': 'Billing View Only',
      'enrollment-admin': 'Enrollment Admin',
      'lr-admin': 'LR Admin',
      LegalResourcesAdmin: 'LR Admin'
    };
    return roleMap[role] || role;
  }

  getRolesDisplayLine(roles: string[] | null | undefined): string {
    const list = roles?.filter(r => r?.length) ?? [];
    if (list.length === 0) {
      return 'User';
    }
    return list.map(r => this.getRoleDisplayName(r)).join(', ');
  }

  // Base menu items with permission requirements
  private baseNavItems: NavItem[] = [
    { label: 'Dashboard', route: '/', icon: 'home' },
    {
      label: 'Members',
      icon: 'groups',
      items: [
        { label: 'Member Management', route: '/members', icon: 'manage_accounts', description: 'Manage members', requiredPermission: 'Enrollment.View', routerLinkExact: true, activeRoutes: ['/group', '/members/send-invitations'] },
        { label: 'Enrollment Queue', route: '/enrollments', icon: 'pending_actions', description: 'Review Employee Enrollments', requiredPermission: 'Enrollment.View', routerLinkExact: true },
        { label: 'Add Member', route: '/members/add-member', icon: 'person_add', description: 'Add a new member', requiredPermission: 'Enrollment.Manage' },
        { label: 'Bulk Import Members', route: '/members/bulk-import', icon: 'upload_file', description: 'Import many members from a spreadsheet', requiredPermission: 'Enrollment.Manage', featureFlag: 'BulkUploadMembers' }
      ]
    },
    {
      label: 'Billing',
      icon: 'credit_card',
      items: [
        { label: 'Invoices', route: '/billing/invoices', icon: 'description', description: 'View and manage invoices', requiredPermission: 'Invoices.View' },
        { label: 'Payment Report', route: '/billing/payment-report', icon: 'storage', description: 'View payment history', requiredPermission: 'PaymentReports.View' }
      ]
    },
    {
      label: 'Resources',
      icon: 'folder_open',
      items: [
        { label: 'Documents', route: '/account/documents', icon: 'description', description: 'View documents and marketing materials', requiredPermission: 'Document.View', featureFlag: 'ClientDocuments' }
      ]
    },
    {
      label: 'Settings/Admin',
      icon: 'admin_panel_settings',
      items: [
        { label: 'Company', route: COMPANIES_NAV_ROUTE, icon: 'business', description: 'Manage Company', requiredPermission: 'Company.Select', activeRoutes: ['/subclients'] },
        { label: 'Users', route: '/users', icon: 'people', description: 'Manage system users', requiredPermission: 'Users.View' }
      ]
    }
  ];

  // Filtered menu items based on user permissions
  navItems = computed(() => {
    const permissions = this.userPermissions();
    const companyLabel = this.companyNavLabel();

    return this.baseNavItems
      .map(item => {
        // If item has sub-items, filter them
        if (item.items) {
          const filteredItems = item.items
            .filter(subItem => {
              // If permission required, check user has it
              if (subItem.requiredPermission && !permissions.includes(subItem.requiredPermission))
                return false;
              // If a feature flag is required, check it is enabled
              if (subItem.featureFlag && !this.featureFlagService.isEnabled(subItem.featureFlag))
                return false;
              return true;
            })
            .map(subItem =>
              subItem.route === COMPANIES_NAV_ROUTE
                ? { ...subItem, label: companyLabel }
                : subItem
            );

          // If no items remain, don't show the parent group
          if (filteredItems.length === 0) return null;

          // Return item with filtered sub-items
          return {
            ...item,
            items: filteredItems
          };
        } else {
          // Single item without sub-items
          // If no permission required, always show
          if (!item.requiredPermission) return item;
          // Check if user has the required permission
          if (permissions.includes(item.requiredPermission)) {
            return item;
          }
          return null;
        }
      })
      .filter((item): item is NavItem => item !== null);
  });

  /** Explicitly set a group's expanded state (driven by panel opened/closed events). */
  setGroupExpanded(groupLabel: string, expanded: boolean): void {
    const updated = new Set(this.expandedGroups());
    if (expanded) {
      updated.add(groupLabel);
    } else {
      updated.delete(groupLabel);
    }
    this.expandedGroups.set(updated);
  }

  /** Single source of truth for both the panel's [expanded] and the chevron direction. */
  isGroupExpanded(groupLabel: string): boolean {
    return this.expandedGroups().has(groupLabel);
  }

  isGroupActive(groupLabel: string): boolean {
    const item = this.navItems().find(i => i.label === groupLabel);
    if (!item?.items) return false;
    const currentUrl = this.router.url;
    return item.items.some(sub =>
      [sub.route, ...(sub.activeRoutes ?? [])].some(r => currentUrl.startsWith(r))
    );
  }

  /** Active state for a sub-menu item, including optional detail-page prefixes. */
  isSubItemActive(sub: NavSubItem): boolean {
    const url = this.router.url.split('?')[0];
    if ((sub.activeRoutes ?? []).some(r => url.startsWith(r))) {
      return true;
    }
    if (sub.routerLinkExact) {
      return url === sub.route || url === `${sub.route}/`;
    }
    return url.startsWith(sub.route);
  }

  /** Every path prefix claimed by a sub-menu item, including detail pages. */
  private allSubRoutes(): string[] {
    return this.baseNavItems.flatMap(i =>
      i.items?.flatMap(s => [s.route, ...(s.activeRoutes ?? [])]) ?? []
    );
  }

  /** Active state for top-level items without sub-items. */
  isItemActive(item: NavItem): boolean {
    const url = this.router.url;

    // Normal items: highlight when the address starts with their route.
    if (item.route !== '/') {
      return url.startsWith(item.route!);
    }

    // Dashboard points at '/', which prefixes every address, so it can't be
    // matched directly. Highlight it only when nothing else claims the URL.
    return !this.allSubRoutes().some(r => url.startsWith(r));
  }

  /** Auto-expands the group containing the current route. Only adds, never removes. */
  private expandActiveGroup(): void {
    const currentUrl = this.router.url;
    const activeGroup = this.baseNavItems.find(item =>
      item.items?.some(subItem =>
        [subItem.route, ...(subItem.activeRoutes ?? [])].some(r => currentUrl.startsWith(r))
      )
    );
    if (activeGroup && !this.expandedGroups().has(activeGroup.label)) {
      this.setGroupExpanded(activeGroup.label, true);
    }
  }
}